import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { customerInfoCaptureModalState } from "atoms";
import { useRecoilState } from "recoil";
import { formatPhoneNumber, formatEmailAddress } from "utils";
import { leadInfo } from "services/post-leadInfoPage";
import { useNavigate } from "react-router-dom";
import { useWrapRequest } from "utils";

const CustomerCaptureInfo: React.FC<{
  item: string;
  type: string;
  coreType: string;
  utmContent: string;
  utmMedium: string;
  utmSource: string;
  utmCampaign: string;
}> = ({ item, type, coreType, utmContent, utmMedium, utmSource, utmCampaign }) => {
  const navigate = useNavigate();
  const [customerInfoCaptureModal, setCustomerInfoCaptureModal] = useRecoilState(customerInfoCaptureModalState);
  const [customerForm, setCustomerForm] = useState({
    firstName: "",
    lastName: "",
    suffix: "",
    email: "",
    phone: "",
    bypassForm: false,
  });
  const emailValidationRegEx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const wrapRequest = useWrapRequest();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCustomerInfoCaptureModal(false);

    wrapRequest(async () => {
      const leadInfoResponse = await leadInfo(customerForm, item, type, coreType, utmContent, utmMedium, utmSource, utmCampaign);

      const { _links: links = {} } = leadInfoResponse;
      const windowLocationHref = links["ec:windowLocation"]?.href;

      if (windowLocationHref) {
        navigate(windowLocationHref);
      }
    });
  };

  const setValidationClassPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.length === 0) {
      return "form-control";
    }

    if (phoneNumber.length > 0 && customerForm.phone.length < 14) {
      return "form-control is-invalid";
    }

    return "form-control is-valid";
  };

  const setValidationClassEmailAddress = (emailAddress: string) => {
    if (emailAddress.length === 0) {
      return "form-control";
    }

    if (emailValidationRegEx.test(emailAddress)) {
      return "form-control is-valid";
    }

    return "form-control is-invalid";
  };

  const setValidationClassFirstName = (firstName: string) => {
    if (firstName.length === 0) {
      return "form-control";
    }

    if (firstName.length > 1 && firstName.length < 35) {
      return "form-control is-valid";
    }

    return "form-control is-invalid";
  };

  const setValidationClassLastName = (lastName: string) => {
    if (lastName.length === 0) {
      return "form-control";
    }

    if (lastName.length > 1 && lastName.length < 31) {
      return "form-control is-valid";
    }

    return "form-control is-invalid";
  };

  const bypassFormField = () => {
    setCustomerForm({ ...customerForm, bypassForm: true });
    const customerFormBypassTrue = { ...customerForm, bypassForm: true };
    wrapRequest(async () => {
      const leadInfoResponse = await leadInfo(customerFormBypassTrue, item, type, coreType, utmContent, utmMedium, utmSource, utmCampaign);

      const { _links: links = {} } = leadInfoResponse;
      const windowLocationHref = links["ec:windowLocation"]?.href;

      if (windowLocationHref) {
        navigate(windowLocationHref);
      }
    });
  };

  useEffect(() => {
    const resetCustomerForm = () => {
      setCustomerForm({
        ...customerForm,
        firstName: "",
        lastName: "",
        suffix: "",
        email: "",
        phone: "",
        bypassForm: false,
      });
    };
    resetCustomerForm();
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <Modal
        show={customerInfoCaptureModal}
        onHide={() => setCustomerInfoCaptureModal(false)}
        backdrop="static"
        size="lg"
        className="customer-capture-form"
      >
        <Modal.Body>
          <>
            <div className="p-5">
              <h3 className="text-center customer-capture-header-text">Stay up to date with Comporium offers for your area.</h3>
              <form className="form-floating mt-5" onSubmit={handleSubmit}>
                <div className="customer-capture-form-fields">
                  <div className="form-floating me-2 mt-2">
                    <input
                      type="text"
                      className={setValidationClassFirstName(customerForm.firstName)}
                      id="firstName"
                      placeholder="First Name"
                      value={customerForm.firstName}
                      maxLength={34}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCustomerForm({
                          ...customerForm,
                          firstName: e.target.value,
                        })
                      }
                    />
                    <label htmlFor="firstName">First Name</label>
                    <div id="validationServer01Feedback" className="invalid-feedback">
                      First Name should be at least 2 characters.
                    </div>
                  </div>
                  <div className="form-floating mt-2 me-2">
                    <input
                      type="text"
                      className={setValidationClassLastName(customerForm.lastName)}
                      id="lastName"
                      placeholder="Last Name"
                      value={customerForm.lastName}
                      maxLength={30}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCustomerForm({
                          ...customerForm,
                          lastName: e.target.value,
                        })
                      }
                    />
                    <label htmlFor="lastName">Last Name</label>
                    <div id="validationServer02Feedback" className="invalid-feedback">
                      Last Name should be at least 2 characters.
                    </div>
                  </div>
                  <div className="form-floating mt-2 capture-form-suffix">
                    <select
                      className="form-select is-valid"
                      id="floatingSelect"
                      value={customerForm.suffix ? customerForm.suffix : ""}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setCustomerForm({
                          ...customerForm,
                          suffix: e.target.value,
                        })
                      }
                    >
                      <option value=""></option>
                      <option value="Jr.">Jr.</option>
                      <option value="Sr.">Sr.</option>
                      <option value="I">I</option>
                      <option value="II">II</option>
                      <option value="III">III</option>
                      <option value="IV">IV</option>
                    </select>
                    <label htmlFor="floatingSelect">Suffix</label>
                  </div>
                </div>
                <div className="customer-capture-form-fields">
                  <div className="form-floating me-2 mt-2">
                    <input
                      type="text"
                      className={setValidationClassPhoneNumber(customerForm.phone)}
                      id="phone"
                      placeholder="Phone"
                      value={formatPhoneNumber(customerForm.phone)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCustomerForm({
                          ...customerForm,
                          phone: formatPhoneNumber(e.target.value),
                        })
                      }
                    />
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <div id="validationServer03Feedback" className="invalid-feedback">
                      Please enter a valid 10 digit number.
                    </div>
                  </div>
                  <div className="form-floating mt-2">
                    <input
                      type="email"
                      className={setValidationClassEmailAddress(customerForm.email)}
                      id="email"
                      placeholder="Email"
                      value={formatEmailAddress(customerForm.email)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCustomerForm({
                          ...customerForm,
                          email: e.target.value,
                        })
                      }
                    />
                    <label htmlFor="email">Email Address</label>
                    <div id="validationServer03Feedback" className="invalid-feedback">
                      Please enter a valid Email Address.
                    </div>
                  </div>
                </div>
                <div className="text-center mt-3">
                  {customerForm.firstName.length < 3 ||
                  customerForm.lastName.length < 3 ||
                  !customerForm.phone ||
                  customerForm.phone.length < 14 ||
                  !customerForm.email ||
                  !emailValidationRegEx.test(customerForm.email) ? (
                    <strong className="text-warning p-1">Please complete all fields before submitting.</strong>
                  ) : null}
                </div>
                <div className="text-center mt-4 mb-4">
                  <button
                    className="btn btn-primary btn-lg"
                    type="submit"
                    disabled={
                      customerForm.firstName.length < 3 ||
                      customerForm.lastName.length < 3 ||
                      !customerForm.phone ||
                      customerForm.phone.length < 14 ||
                      !customerForm.email ||
                      !emailValidationRegEx.test(customerForm.email)
                    }
                  >
                    Submit
                  </button>
                </div>
              </form>
              <p className="disclaimer-text">
                By clicking “submit” above, I consent to receive from Comporium e-mails, telemarketing and SMS text messages, which could result in
                wireless charges at the number above. I also agree to the{" "}
                <a href="https://www.comporium.com/terms-and-conditions/" rel="noreferrer" target="_blank">
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a href="https://www.comporium.com/privacy-policy/" rel="noreferrer" target="_blank">
                  Privacy Policy
                </a>
                , which are linked at the bottom of this page.
              </p>
              <p
                className="close-link"
                onClick={(e: React.MouseEvent<HTMLParagraphElement>) => {
                  bypassFormField();
                }}
              >
                Or skip this step
              </p>
            </div>
          </>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CustomerCaptureInfo;
